import * as React from "react";
import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useGetPopularPeopleQuery } from "../../store/api/genreApi";
import PersonCard from "../../components/PersonCard/index.tsx";

export type PopularPersonRow = {
  id: number;
  name: string;
  profile_path?: string | null;
  known_for_department?: string;
};

const PeoplePage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allPeople, setAllPeople] = useState<PopularPersonRow[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { data, isLoading, isFetching, error, refetch } = useGetPopularPeopleQuery({
    page: currentPage,
  });

  useEffect(() => {
    if (!data?.results) {
      return;
    }
    if (data.page !== currentPage) {
      return;
    }
    const rows = data.results as PopularPersonRow[];
    if (currentPage === 1) {
      setAllPeople(rows);
    } else {
      setAllPeople((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        const next = rows.filter((p) => !seen.has(p.id));
        return [...prev, ...next];
      });
    }
    setHasMore(currentPage < (data.totalPages || 0));
  }, [data, currentPage]);

  const observer = useRef<IntersectionObserver>();
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }
      if (isFetching) {
        return;
      }
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setCurrentPage((p) => p + 1);
        }
      });
      if (node) {
        observer.current.observe(node);
      }
    },
    [isFetching, hasMore],
  );

  if (error) {
    return (
      <section className="container page-section people-page">
        <h2 className="page-section-title">Popular people</h2>
        <p className="people-page-error">Something went wrong. Please try again.</p>
        <button type="button" className="btn-pill" onClick={() => refetch()}>
          Retry
        </button>
      </section>
    );
  }

  if (isLoading && currentPage === 1) {
    return (
      <section className="container page-section people-page">
        <h2 className="page-section-title">Popular people</h2>
        <p className="people-page-status">Loading…</p>
      </section>
    );
  }

  return (
    <section className="container page-section people-page">
      <div className="people-page-toolbar">
        <h2 className="page-section-title">Popular people</h2>
        <Link to="/" className="btn-pill people-page-back">
          ← Home
        </Link>
      </div>
      <p className="people-page-intro">
        Trending performers on TMDB. Scroll down to load more.
      </p>

      {allPeople.length === 0 ? (
        <p className="people-page-status">No people found.</p>
      ) : (
        <div className="people-grid">
          {allPeople.map((person, index) => (
            <PersonCard
              key={person.id}
              person={person}
              innerRef={
                index === allPeople.length - 1 ? lastItemRef : undefined
              }
            />
          ))}
        </div>
      )}

      {isFetching && currentPage > 1 ? (
        <p className="people-page-status people-page-more">Loading more…</p>
      ) : null}

      {data && allPeople.length > 0 ? (
        <p className="people-page-meta">
          Showing {allPeople.length}
          {data.totalResults ? ` of ${data.totalResults}` : ""}
          {hasMore ? " · Scroll for more" : ""}
        </p>
      ) : null}
    </section>
  );
};

export default PeoplePage;
